import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SUPPORTED_LANGUAGES } from '../../i18n/config';
import { setStoredLanguage } from '../../services/languageStorage';

/**
 * DE/EN switcher.
 *
 * Language names are rendered as endonyms ("Deutsch", "English") and are
 * therefore identical in both locale files on purpose: someone who cannot read
 * the language the UI is currently in must still be able to recognise their
 * own.
 */
const LanguageSwitcher = ({ className = '' }) => {
  const { t, i18n } = useTranslation('common');

  const handleChange = (language) => {
    // Persist BEFORE switching: if changeLanguage() throws for any reason the
    // user's intent still survives the next reload, rather than silently
    // reverting to the default.
    setStoredLanguage(language);
    i18n.changeLanguage(language);
  };

  return (
    <Select value={i18n.resolvedLanguage} onValueChange={handleChange}>
      <SelectTrigger
        aria-label={t('language.switcherLabel')}
        title={t('language.switcherLabel')}
        // Mirrors the sibling toolbar buttons exactly (Configs / filter / file
        // manager / component library all use
        // "px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-md").
        // h-auto and text-base are needed to override SelectTrigger's own h-9
        // and text-sm, which otherwise render this control ~6px shorter than
        // its neighbours. cn() is tailwind-merge, so these win.
        className={`h-auto w-auto gap-2 px-4 py-2 text-base bg-white border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-shadow ${className}`}
      >
        <Languages className="w-5 h-5 flex-shrink-0" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LANGUAGES.map((language) => (
          <SelectItem key={language} value={language}>
            {t(`language.${language}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSwitcher;
